import {
	Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren, Input, HostListener,
	Output, EventEmitter
} from '@angular/core';
import { Calendar } from './calendar';
import { SessionService } from '../../services/session.service';
import { DateFormatService } from '../../services/date-format.service';
import moment from 'moment';
import { SHARED_MODULES } from '../../../shared.';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { PhButtonComponent } from '../ph-button/ph-button.component';

@Component({
    selector: 'app-picker',
    templateUrl: './picker.component.html',
    styleUrls: ['./picker.component.scss'],
	imports: [SHARED_MODULES, ClickOutsideDirective, FaIconComponent, PhButtonComponent, PhIconWrapperComponent],
    standalone: true
})
export class PickerComponent implements OnInit {
	@ViewChild('monthScroll') monthScroll: ElementRef;
	@ViewChildren('yearMonths') yearMonths: QueryList<ElementRef>;
	@ViewChildren('monthDay') monthDay: QueryList<ElementRef>;

	firstClickMonth: boolean = false;
	calendar: Calendar;
	monthSlider: boolean = false;
	inputDate: string;
	showPicker: boolean = false;
	ranges: any;
	dateRange: { date_from: string | moment.Moment | Date, date_to: string | moment.Moment | Date } = {
		date_from: moment().subtract(29, 'day'),
		date_to: moment()
	};
	@Input() minDate: string | moment.Moment | Date = moment().subtract(5, 'year');
	@Input() maxDate: string | moment.Moment | Date = moment().add(1, 'year');
	@Input() showInput: boolean = true;
	@Input() viewFormat: string = 'DD-MM-YYYY';
	@Input() inputOutputFormat: string = 'YYYY-MM-DD';
	@Input() customClass: string = 'bottom';
	@Input() disabled: boolean = false;
	@Input() autoLayoutPicker: boolean = false;
	@Input() topPosition: boolean = false;
	@Output() setDate = new EventEmitter<any>();
	@Output() setStatus = new EventEmitter<any>();

	constructor(
		private session: SessionService,
		private dateFormat: DateFormatService,
	) {
	}

	@Input('dateRange') set setDateRange(value: {
		date_from: string | moment.Moment | Date,
		date_to: string | moment.Moment | Date
	}) {
		this.dateRange = value;
		this.ngOnInit();
	}

	@Input('openPicker') set setOpenPicker(value) {
		if (value) setTimeout(() => {
			this.activate();
		});
	}

	ngAfterViewInit() {
		this.showPicker = false;
	}

	updateInput() {
		const start: string = this.dateFormat.formatDate(this.calendar.start, 'date');
		const end: string = this.dateFormat.formatDate(this.calendar.end, 'date');
		this.inputDate = `${start} - ${end}`;
	}

	ngOnInit() {
		this.calendar = new Calendar({
			minDate: this.minDate || moment(this.session.defaults.date_from),
			maxDate: this.maxDate || moment(this.session.defaults.date_to),
			start: (moment(this.dateRange.date_from).isValid()) ? moment(this.dateRange.date_from) : moment().subtract(29, 'day'),
			end: (moment(this.dateRange.date_to).isValid()) ? moment(this.dateRange.date_to) : moment()
		});
		this.updateInput();
		this.ranges = {
			this_year: {
				range: [this.calendar.maxDate.clone().startOf('year'), this.calendar.maxDate.clone()],
				active: false
			},
			this_month: {
				range: [this.calendar.maxDate.clone().startOf('month'), this.calendar.maxDate.clone()],
				active: false
			},
			all_period: { range: [this.calendar.minDate, this.calendar.maxDate], active: false },
			last_90_days: {
				range: [this.calendar.maxDate.clone().subtract(89, 'day'), this.calendar.maxDate],
				active: false
			},
			last_30_days: {
				range: [this.calendar.maxDate.clone().subtract(29, 'day'), this.calendar.maxDate],
				active: false
			}
		};
	}

	buttonsActiveFalse() {
		Object.entries(this.ranges).forEach(([key, value]) => {
			this.ranges[key].active = false;
		});
	}

	checkActiveButton(name) {
		this.buttonsActiveFalse();
		this.ranges[name].active = true;

	}

	setRange(name: string) {
		if (this.ranges.hasOwnProperty(name)) this.calendar.setRange(this.ranges[name].range[0], this.ranges[name].range[1]);
		// this.updateInput();
		this.checkActiveButton(name);
	}

	activate() {
		if (this.disabled) return;

		this.showPicker = !this.showPicker;
		if (this.showPicker) this.scrollToActive();
		this.setStatus.emit(this.showPicker);
	}

	saveDate() {
		if (this.calendar.end == null) this.calendar.end = this.calendar.start.clone();
		if (moment.isMoment(this.dateRange.date_from)) this.dateRange.date_from = this.calendar.start.clone();
		if (moment.isDate(this.dateRange.date_from)) this.dateRange.date_from = this.calendar.start.clone().toDate();
		if (typeof this.dateRange.date_from == 'string') this.dateRange.date_from = this.calendar.start.clone().format(this.inputOutputFormat);

		if (moment.isMoment(this.dateRange.date_to)) this.dateRange.date_to = this.calendar.end.clone();
		if (moment.isDate(this.dateRange.date_to)) this.dateRange.date_to = this.calendar.end.clone().toDate();
		if (typeof this.dateRange.date_to == 'string') this.dateRange.date_to = this.calendar.end.clone().format(this.inputOutputFormat);
	}

	save() {
		this.saveDate();
		this.updateInput();
		this.setDate.emit(this.dateRange);
		this.showPicker = false;
		this.setStatus.emit(this.showPicker);
	}

	cancel() {
		this.calendar.start = moment(this.dateRange.date_from);
		this.calendar.end = moment(this.dateRange.date_to);
		this.calendar.checkStartEndDays(this.calendar.start, this.calendar.end);
		this.showPicker = false;
		this.setStatus.emit(this.showPicker);
	}

	scrollToActiveMonth() {
		const elements = this.monthDay.filter((i) =>
			i.nativeElement.className.includes('checkedMonth') &&
			i.nativeElement.className.includes('month')
		);
		const element = elements[elements.length - 1]?.nativeElement as HTMLElement;
		if (!element) return;

		const container = (this.monthScroll?.nativeElement ?? element.closest('.viewDays')) as HTMLElement;
		if (!container) return;

		this.scrollElementIntoContainer(element, container);
	}

	scrollToActiveYear() {
		const elements = this.yearMonths.filter((i) => i.nativeElement.className.includes('check'));
		const element = elements[elements.length - 1]?.nativeElement as HTMLElement;
		if (!element) return;

		const container = element.closest('.viewMonth') as HTMLElement;
		if (!container) return;

		this.scrollElementIntoContainer(element, container);
	}

	scrollToActive() {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.scrollToActiveMonth();
				if (this.monthSlider) {
					this.scrollToActiveYear();
				}
			});
		});
	}

	private scrollElementIntoContainer(element: HTMLElement, container: HTMLElement): void {
		const elementRect = element.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();

		if (elementRect.top < containerRect.top) {
			container.scrollTop -= containerRect.top - elementRect.top;
		} else if (elementRect.bottom > containerRect.bottom) {
			container.scrollTop += elementRect.bottom - containerRect.bottom;
		}
	}

	monthView(event) {
		this.monthSlider = event.checked;
		this.scrollToActive();
		this.firstClickMonth = false;
	}

	clickMonth(day: moment.Moment) {
		this.firstClickMonth = !this.firstClickMonth;
		this.buttonsActiveFalse();
		this.calendar.checkMonth(day, this.firstClickMonth);
	}

	clickDay(day: moment.Moment) {
		this.buttonsActiveFalse();
		this.calendar.checkDay(day);
	}
}